import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { AssetTypeCard } from './AssetTypeCard';
import { STARTING_METHODS } from '@/lib/assetTypes';

/**
 * Phase 1 — Asset-First Create flow.
 * Step 1: Pick what to create (asset type, 8 options, capability hints).
 * Step 2: Pick a starting method (Blank, Excel/Word Import, Sample, Template, Duplicate, AI grayed).
 */
export function CreateAssessmentDialog({ open, onOpenChange, onCreated }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [meta, setMeta] = useState(null);
  const [pickedType, setPickedType] = useState(null);
  const [pickedMethod, setPickedMethod] = useState('blank');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setPickedType(null);
    setPickedMethod('blank');
    setName('');
    adminApi.studioMeta()
      .then(setMeta)
      .catch((err) => toast.error(`Failed to load studio meta: ${err.message || err}`));
  }, [open]);

  const pickedAsset = useMemo(
    () => meta?.asset_types?.find((a) => a.code === pickedType) || null,
    [meta, pickedType],
  );

  const handleSubmit = async () => {
    if (!pickedType || !name.trim()) return;
    setSaving(true);
    try {
      const { assessment } = await adminApi.studioCreateAssessment({
        type: pickedType,
        name: name.trim(),
        starting_method: pickedMethod,
      });
      toast.success(t('studio.create.success', { defaultValue: 'Assessment created.' }));
      onOpenChange(false);
      if (onCreated) onCreated(assessment);
      if (pickedMethod === 'excel_import' || pickedMethod === 'word_import') {
        // route the user to the existing import wizard (kept under questionnaires/import for now)
        navigate('/admin/questionnaires/import');
      } else {
        navigate(`/admin/studio/edit/${assessment.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="studio-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {step === 1
              ? t('studio.create.step1Title', { defaultValue: 'What would you like to create?' })
              : t('studio.create.step2Title', { defaultValue: 'How would you like to start?' })}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {step === 1
              ? t('studio.create.step1Sub', { defaultValue: 'Pick an asset type — smart defaults will be applied.' })
              : t('studio.create.step2Sub', { defaultValue: 'Choose a starting method for this assessment.' })}
          </p>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-2" data-testid="studio-asset-grid">
            {(meta?.asset_types || []).map((a) => (
              <AssetTypeCard
                key={a.code}
                asset={a}
                selected={pickedType === a.code}
                onSelect={setPickedType}
              />
            ))}
          </div>
        )}

        {step === 2 && pickedAsset && (
          <div className="space-y-4 my-2">
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm">
              <span className="font-semibold">
                {i18n.language?.startsWith('es') ? pickedAsset.label_es : pickedAsset.label_en}
              </span>
              <span className="text-muted-foreground ml-2">·</span>
              <span className="text-muted-foreground ml-2">
                {t('studio.create.suiteHint', { defaultValue: 'Suite' })}: {(pickedAsset.defaults?.suite_codes || []).join(', ') || '—'}
              </span>
            </div>

            <div>
              <Label htmlFor="studio-create-name">
                {t('studio.create.nameLabel', { defaultValue: 'Assessment name' })}
              </Label>
              <Input
                id="studio-create-name"
                data-testid="studio-create-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('studio.create.namePlaceholder', { defaultValue: 'e.g. Leadership Decision Making' })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>{t('studio.create.methodLabel', { defaultValue: 'Starting method' })}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="studio-starting-methods">
                {STARTING_METHODS.map((m) => {
                  const disabled = m.disabled;
                  const selected = pickedMethod === m.code;
                  return (
                    <button
                      type="button"
                      key={m.code}
                      disabled={disabled}
                      data-testid={`studio-method-${m.code}`}
                      onClick={() => !disabled && setPickedMethod(m.code)}
                      className={[
                        'text-left p-3 rounded-md border-2 text-sm transition-all',
                        selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
                        disabled ? 'opacity-40 cursor-not-allowed' : '',
                      ].join(' ')}
                    >
                      <div className="font-medium">
                        {t(m.labelKey, { defaultValue: m.code.replace('_', ' ') })}
                      </div>
                      {disabled && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {t('studio.starting.aiHint', { defaultValue: 'Coming in Phase 9' })}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button
              variant="ghost"
              onClick={() => setStep(1)}
              data-testid="studio-create-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t('common.back', { defaultValue: 'Back' })}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="studio-create-cancel"
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {step === 1 ? (
            <Button
              onClick={() => setStep(2)}
              disabled={!pickedType}
              data-testid="studio-create-next"
            >
              {t('common.next', { defaultValue: 'Next' })}
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={saving || !name.trim() || !pickedType}
              data-testid="studio-create-submit"
            >
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {t('studio.create.submit', { defaultValue: 'Create assessment' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateAssessmentDialog;
